/**
 * Merch Preview Module
 *
 * Shows CSS composite mockups of fractal artwork on merchandise.
 * Overlays the parcel's WASM-rendered thumbnail onto product template photos.
 *
 * Order flow: color picker (if applicable) → size picker → country selector →
 * shipping calculation → high-res WASM render → upload to Supabase Storage →
 * Stripe Checkout (with dynamic shipping) → Printful fulfillment via webhook.
 *
 * Prices are fetched dynamically from /api/products at modal open time.
 */
import type { Parcel } from "./store";
import { renderParcelThumbnailCached } from "./thumbnailRenderer";
import { renderParcelHighRes, renderAndCompose } from "./thumbnailRenderer";
import type { CompositionConfig } from "./thumbnailRenderer";
import { uploadMerchImage } from "./db";
import { supabase } from "./api";
import { trackEvent } from "./analytics";

// Product template image paths (served by webpack file-loader via static.ts imports)
const TSHIRT_TEMPLATE = "static/tshirt.webp";
const SWEATSHIRT_TEMPLATE = "static/tshirt.webp"; // Placeholder until sweatshirt mockup is provided
const MUG_TEMPLATE = "static/mug.jpg";

/** Countries available for shipping (matches Stripe allowed_countries) */
const SHIPPING_COUNTRIES: Record<string, string> = {
  US: "United States",
  CA: "Canada",
  GB: "United Kingdom",
  DE: "Germany",
  FR: "France",
  ES: "Spain",
  IT: "Italy",
  NL: "Netherlands",
  BE: "Belgium",
  AT: "Austria",
  CH: "Switzerland",
  SE: "Sweden",
  NO: "Norway",
  DK: "Denmark",
  FI: "Finland",
  IE: "Ireland",
  PT: "Portugal",
  PL: "Poland",
  CZ: "Czech Republic",
  AU: "Australia",
  NZ: "New Zealand",
  JP: "Japan",
};

/** Local product config (template, overlay, render dimensions) */
interface LocalProductDef {
  template: string;
  overlay: { top: string; left: string; width: string; height: string };
  printWidth: number;
  printHeight: number;
  composition?: CompositionConfig;
  isMug?: boolean;
}

const LOCAL_PRODUCTS: Record<string, LocalProductDef> = {
  tshirt: {
    template: TSHIRT_TEMPLATE,
    overlay: { top: "25%", left: "31%", width: "38%", height: "21.4%" },
    printWidth: 4500,
    printHeight: 5400,
    composition: { background: "transparent", verticalAlign: "top" },
  },
  sweatshirt: {
    template: SWEATSHIRT_TEMPLATE,
    overlay: { top: "25%", left: "31%", width: "38%", height: "21.4%" },
    printWidth: 4500,
    printHeight: 5400,
    composition: { background: "transparent", verticalAlign: "top" },
  },
  mug: {
    template: MUG_TEMPLATE,
    overlay: { top: "35%", left: "18%", width: "64%", height: "30%" },
    printWidth: 2700,
    printHeight: 1080,
    isMug: true,
  },
};

/** Color info from API */
interface ApiColor {
  name: string;
  hex: string;
  sizes: string[];
}

/** Product info from API */
interface ApiProduct {
  id: string;
  name: string;
  fromPriceEurCents: number;
  colors: ApiColor[];
  hasVariants: boolean;
}

let activeParcel: Parcel | null = null;
let cachedProducts: ApiProduct[] | null = null;

function formatPrice(cents: number): string {
  return `\u20ac${(cents / 100).toFixed(cents % 100 === 0 ? 0 : 2)}`;
}

/** Fetch product catalog from API */
async function fetchProducts(): Promise<ApiProduct[]> {
  if (cachedProducts) return cachedProducts;
  const res = await fetch("/api/products");
  if (!res.ok) throw new Error("Failed to load product catalog");
  const data = await res.json();
  cachedProducts = data.products;
  return data.products;
}

/**
 * Open the merch preview modal for a given parcel.
 * Fetches dynamic prices and renders a preview thumbnail.
 */
export async function openMerchPreview(parcel: Parcel): Promise<void> {
  activeParcel = parcel;

  const modal = document.getElementById("merchModal") as HTMLDialogElement | null;
  const parcelName = document.getElementById("merchParcelName");
  const grid = document.getElementById("merchGrid");
  if (!modal || !grid) return;

  if (parcelName) parcelName.textContent = parcel.name || "Untitled";
  grid.innerHTML = `<div class="merch-loading"><span class="thumbnail-spinner"></span></div>`;
  modal.showModal();
  trackEvent("merch_preview_open", { parcelId: parcel.id });

  try {
    // Fetch prices and render thumbnail in parallel
    const [products, thumbnailUrl] = await Promise.all([
      fetchProducts(),
      renderParcelThumbnailCached(parcel, 256),
    ]);

    grid.innerHTML = products
      .filter((p) => LOCAL_PRODUCTS[p.id])
      .map((p) => renderProductCard(p, thumbnailUrl))
      .join("");
    attachOrderListeners(grid);
  } catch (error) {
    console.error("[Merch] Failed to load merch preview:", error);
    grid.innerHTML = `<p class="secondary">Failed to generate preview. Please try again.</p>`;
  }
}

function renderProductCard(product: ApiProduct, fractalUrl: string): string {
  const local = LOCAL_PRODUCTS[product.id];
  if (!local) return "";
  const { overlay } = local;
  const fromPrice = formatPrice(product.fromPriceEurCents);

  const overlayClass = local.isMug ? "merch-fractal-overlay merch-fractal-overlay--mug" : "merch-fractal-overlay";

  return `
    <div class="merch-card" data-product="${product.id}">
      <div class="merch-preview">
        <img src="${local.template}" class="merch-template" alt="${product.name} template" crossorigin="anonymous" />
        <img src="${fractalUrl}" class="${overlayClass}"
             style="top:${overlay.top}; left:${overlay.left}; width:${overlay.width}; height:${overlay.height};"
             alt="Fractal preview" />
      </div>
      <div class="merch-info">
        <span class="merch-name">${product.name}</span>
        <span class="merch-price">from ${fromPrice} <span class="secondary">+ shipping</span></span>
      </div>
      ${product.hasVariants ? `
        <div class="merch-color-picker hidden" data-product="${product.id}">
          <span class="merch-picker-label">Color: <span class="merch-selected-color-name"></span></span>
          <div class="merch-color-swatches">
            ${product.colors.map((c) => `<button class="merch-color-swatch" data-color="${c.name}" style="background:${c.hex}" title="${c.name}"></button>`).join("")}
          </div>
        </div>
        <div class="merch-size-picker hidden" data-product="${product.id}">
          <!-- Sizes populated dynamically when color is picked -->
        </div>
      ` : ""}
      <div class="merch-shipping-section hidden" data-shipping-section="${product.id}">
        <select class="merch-country-select" data-country-select="${product.id}">
          <option value="">Select your country</option>
          ${Object.entries(SHIPPING_COUNTRIES).map(([code, name]) => `<option value="${code}">${name}</option>`).join("")}
        </select>
        <div class="merch-shipping-result hidden" data-shipping-result="${product.id}"></div>
      </div>
      <button class="merch-order-btn" data-product="${product.id}">Order from ${fromPrice}</button>
    </div>
  `;
}

function attachOrderListeners(grid: HTMLElement): void {
  grid.querySelectorAll<HTMLButtonElement>(".merch-order-btn").forEach((btn) => {
    btn.addEventListener("click", () => handleOrderClick(btn));
  });
}

async function handleOrderClick(btn: HTMLButtonElement): Promise<void> {
  const productId = btn.dataset.product;
  if (!productId || !activeParcel || !cachedProducts) return;

  const product = cachedProducts.find((p) => p.id === productId);
  if (!product) return;

  const card = btn.closest(".merch-card") as HTMLElement;

  // Step 1: If product has color variants, show color picker first
  if (product.hasVariants) {
    const colorPicker = card.querySelector<HTMLElement>(".merch-color-picker");
    if (colorPicker && colorPicker.classList.contains("hidden")) {
      colorPicker.classList.remove("hidden");
      btn.textContent = "Pick a color";
      btn.disabled = true;

      colorPicker.querySelectorAll<HTMLButtonElement>(".merch-color-swatch").forEach((swatch) => {
        swatch.addEventListener("click", () => {
          // Deselect all swatches, select this one
          colorPicker.querySelectorAll(".merch-color-swatch").forEach((s) => s.classList.remove("selected"));
          swatch.classList.add("selected");

          const colorName = swatch.dataset.color!;
          card.dataset.selectedColor = colorName;

          // Update color name label
          const nameLabel = colorPicker.querySelector(".merch-selected-color-name");
          if (nameLabel) nameLabel.textContent = colorName;

          // Show size picker with sizes for this color
          const color = product.colors.find((c) => c.name === colorName);
          if (color) {
            showSizePicker(product, card, btn, color.sizes);
          }
        });
      });
      return;
    }
  }

  // Products without variants go directly to shipping
  showShippingStep(product, card, btn);
}

function showSizePicker(product: ApiProduct, card: HTMLElement, btn: HTMLButtonElement, sizes: string[]): void {
  const sizePicker = card.querySelector<HTMLElement>(".merch-size-picker");
  if (!sizePicker) return;

  sizePicker.innerHTML = sizes
    .map((s) => `<button class="merch-variant-btn" data-size="${s}">${s}</button>`)
    .join("");
  sizePicker.classList.remove("hidden");
  btn.textContent = "Pick a size";
  btn.disabled = true;

  sizePicker.querySelectorAll<HTMLButtonElement>(".merch-variant-btn").forEach((sizeBtn) => {
    sizeBtn.addEventListener("click", () => {
      sizePicker.querySelectorAll(".merch-variant-btn").forEach((b) => b.classList.remove("selected"));
      sizeBtn.classList.add("selected");

      card.dataset.selectedSize = sizeBtn.dataset.size!;
      showShippingStep(product, card, btn);
    });
  });
}

function showShippingStep(product: ApiProduct, card: HTMLElement, btn: HTMLButtonElement): void {
  const shippingSection = card.querySelector<HTMLElement>(`[data-shipping-section="${product.id}"]`);
  if (!shippingSection) return;

  // If already visible, the user already has shipping info — proceed to order
  if (!shippingSection.classList.contains("hidden")) {
    const shippingCents = parseInt(card.dataset.shippingCents || "0", 10);
    const countryCode = card.dataset.selectedCountry || "";
    if (shippingCents > 0 && countryCode) {
      startOrderFlow(product, card, btn, shippingCents, countryCode);
    }
    return;
  }

  shippingSection.classList.remove("hidden");
  btn.textContent = "Select country for shipping";
  btn.disabled = true;

  const select = card.querySelector<HTMLSelectElement>(`[data-country-select="${product.id}"]`);
  if (!select) return;

  select.addEventListener("change", async () => {
    const countryCode = select.value;
    if (!countryCode) return;

    const resultDiv = card.querySelector<HTMLElement>(`[data-shipping-result="${product.id}"]`);
    if (!resultDiv) return;

    resultDiv.classList.remove("hidden");
    resultDiv.innerHTML = `<span class="thumbnail-spinner"></span> Calculating shipping...`;

    try {
      const colorName = card.dataset.selectedColor || undefined;
      const sizeName = card.dataset.selectedSize || undefined;
      const res = await fetch("/api/calculate-shipping", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          productType: product.id,
          colorName,
          sizeName,
          countryCode,
        }),
      });

      if (!res.ok) throw new Error("Failed to get shipping rates");
      const data = await res.json();
      const option = data.shippingOptions?.[0];
      if (!option) throw new Error("No shipping available to this country");

      const shippingCents = Math.round(parseFloat(option.rate) * 100);

      // Compute exact product price from API (we'll re-fetch or compute locally)
      // For now use the fromPrice as a base — the exact price is computed server-side at checkout
      const productPriceCents = product.fromPriceEurCents; // approximate for display
      const totalCents = productPriceCents + shippingCents;

      card.dataset.shippingCents = String(shippingCents);
      card.dataset.selectedCountry = countryCode;

      resultDiv.innerHTML = `
        <div class="merch-price-breakdown">
          <span>Product: ~${formatPrice(productPriceCents)}</span>
          <span>Shipping: ${formatPrice(shippingCents)}</span>
          <strong>Total: ~${formatPrice(totalCents)}</strong>
          <span class="secondary">${option.name.trim()}</span>
        </div>
      `;

      btn.textContent = `Proceed to checkout — ~${formatPrice(totalCents)}`;
      btn.disabled = false;

      // Clicking the button now starts the order flow
      const newHandler = () => {
        btn.removeEventListener("click", newHandler);
        startOrderFlow(product, card, btn, shippingCents, countryCode);
      };
      btn.addEventListener("click", newHandler);
    } catch (error) {
      console.error("[Merch] Shipping calculation failed:", error);
      resultDiv.innerHTML = `<span class="secondary">Shipping unavailable for this country. Try another.</span>`;
    }
  });
}

async function startOrderFlow(
  product: ApiProduct,
  card: HTMLElement,
  btn: HTMLButtonElement,
  shippingRateCents?: number,
  countryCode?: string,
): Promise<void> {
  if (!activeParcel) return;
  const parcel = activeParcel;
  const local = LOCAL_PRODUCTS[product.id];
  if (!local) return;

  const colorName = card.dataset.selectedColor || undefined;
  const sizeName = card.dataset.selectedSize || undefined;

  trackEvent("merch_order_start", { product: product.id, color: colorName || "", size: sizeName || "" });

  // Disable all order buttons in the modal during the flow
  const allBtns = card.closest(".merch-grid")?.querySelectorAll<HTMLButtonElement>(".merch-order-btn");
  allBtns?.forEach((b) => (b.disabled = true));

  // Show progress in the button area
  const progressEl = document.createElement("div");
  progressEl.className = "merch-progress";
  progressEl.innerHTML = `<span class="thumbnail-spinner"></span><span class="merch-progress-text">Rendering...</span>`;
  btn.replaceWith(progressEl);

  const setProgress = (text: string) => {
    const textEl = progressEl.querySelector(".merch-progress-text");
    if (textEl) textEl.textContent = text;
  };

  try {
    // Check auth
    if (!supabase) throw new Error("Not connected to server");
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error("Please log in to order merch");

    // Step 1: High-res render
    setProgress("Rendering high-res image...");
    const blob = local.composition
      ? await renderAndCompose(parcel, local.printWidth, local.printHeight, local.composition)
      : await renderParcelHighRes(parcel, local.printWidth, local.printHeight);
    console.log(`[Merch] Rendered ${local.printWidth}x${local.printHeight}, ${(blob.size / 1024 / 1024).toFixed(1)} MB`);

    // Step 2: Upload to Supabase Storage
    setProgress("Uploading...");
    const imageUrl = await uploadMerchImage(parcel.id, product.id, blob);
    if (!imageUrl) throw new Error("Failed to upload image");
    console.log("[Merch] Uploaded:", imageUrl);

    // Step 3: Create checkout session with shipping
    setProgress("Redirecting to checkout...");
    const response = await fetch("/api/create-merch-checkout", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({
        productType: product.id,
        parcelId: parcel.id,
        imageUrl,
        colorName,
        sizeName,
        shippingRateCents: shippingRateCents || 0,
        countryCode: countryCode || undefined,
      }),
    });

    const data = await response.json();
    if (!response.ok) throw new Error(data.error || "Failed to create checkout");

    // Step 4: Redirect to Stripe
    if (data.url) {
      trackEvent("merch_checkout_redirect", { product: product.id });
      window.location.href = data.url;
    }
  } catch (error) {
    console.error("[Merch] Order flow failed:", error);
    alert(error instanceof Error ? error.message : "Order failed. Please try again.");

    // Reset UI: replace progress with order button
    const fromPrice = formatPrice(product.fromPriceEurCents);
    const newBtn = document.createElement("button");
    newBtn.className = "merch-order-btn";
    newBtn.dataset.product = product.id;
    newBtn.textContent = `Order from ${fromPrice}`;
    newBtn.addEventListener("click", () => handleOrderClick(newBtn));
    progressEl.replaceWith(newBtn);

    // Re-enable all buttons
    allBtns?.forEach((b) => (b.disabled = false));
  }
}

/**
 * Initialize merch modal event handlers.
 * Call once at app startup.
 */
export function initMerchPreview(): void {
  const modal = document.getElementById("merchModal") as HTMLDialogElement | null;
  if (!modal) return;

  const closeBtn = document.getElementById("merchClose");
  closeBtn?.addEventListener("click", () => modal.close());

  // Close on backdrop click
  modal.addEventListener("click", (e) => {
    if (e.target === modal) modal.close();
  });
}
